import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Huntsville = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.huntsville-on:About Huntsville Paragraph 1">
            Huntsville, located in the Muskoka region of{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText>,
            Canada, is a picturesque town renowned for its natural beauty and
            outdoor allure. Nestled amidst lakes, forests, and rolling hills, it
            serves as the &quot;Gateway to Algonquin Park,&quot; a renowned
            wilderness area. Outdoor enthusiasts flock to Huntsville for hiking,
            camping, canoeing, and fishing in Algonquin Park and its surrounding
            lakes. The town boasts a thriving arts and cultural scene, with
            galleries and events that draw inspiration from the famed Group of
            Seven painters.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.huntsville-on:About Huntsville Paragraph 2">
            Huntsville’s heritage is preserved in its historic downtown and the
            Muskoka Heritage Place. Festivals like the Huntsville Festival of
            the Arts and Canada Day celebrations infuse vibrancy into the
            community. Its proximity to the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area</LinkText> makes
            it an attractive retreat for urbanites seeking a tranquil escape.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$900 - $1,900',
          bed2: '$1,200 - $2,200',
          bed3: '$2,500 - $3,500'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.huntsville-on:Apartment Hunting in Huntsville Paragraph 1">
            Huntsville is an attractive city of Ontario known for its thriving
            community and diverse housing options. Rental prices in Huntsville
            are definitely more affordable than metropolitan cities like
            Toronto. As a result, finding{' '}
            <LinkText href={links.under1000}>
              cheap rentals in Huntsville
            </LinkText>
            , especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , are much easier. If you’re not sure if you want to reside within
            or outside of Greater Toronto Area, you can refer to this guide
            highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.huntsville-on:Apartments for rent Huntsville Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Hunstville
              </LinkText>{' '}
              are not as prevalent as in larger cities. There are several
              apartment complexes and multi-story buildings with units that are
              often sought by individuals or small families looking for
              convenient, low-maintenance living, typically closer to amenities.
              The prices are generally more affordable than renting houses.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.huntsville-on:Houses for rent Huntsville Paragraph">
              Single-family{' '}
              <LinkText href={links.house}>houses for rent</LinkText> are quite
              common in Huntsville. They are typically suitable for those who
              are seeking roommates or families who require more space, privacy,
              and access to outdoor areas. These are more popular among
              long-term residents. The rental prices for houses can vary
              significantly based on size, condition, and location, with
              waterfront properties commanding higher prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.huntsville-on:Townhouses for rent Huntsville Paragraph">
              Townhouses are less common in Huntsville. However, they combine
              the best qualities of both apartments and whole houses. Typically
              less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in Huntsville
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.huntsville-on:Condos for rent Huntsville Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Huntsville
              </LinkText>{' '}
              are available but are not as widespread. They offer a more upscale
              and maintenance-free lifestyle, often with additional amenities
              like fitness centers or pools. Typically, they are popular among
              professionals or small families. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.huntsville-on:Short term rentals Huntsville Paragraph">
              Huntsville, known for its natural beauty and proximity to
              Algonquin Provincial Park, sees fluctuating rental demand, with a
              noticeable increase during tourist seasons rather than academic
              periods. While the town has local employment opportunities,
              primarily in the service, tourism, and retail sectors, it isn’t
              characterized by a robust presence of academic institutions or a
              vast array of job options compared to larger cities.{' '}
              <LinkText href={links.shortTerm}>
                Short-term rentals in Huntsville
              </LinkText>{' '}
              gain popularity, especially in the summer, catering to tourists
              and seasonal workers. These arrangements offer flexibility, which
              is beneficial for individuals seeking temporary accommodation or
              those on working holidays, contributing to the town’s unique
              rental market dynamics. This is something to factor in when
              deciding whether or not short-term rentals are right for you.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.huntsville-on:Living in Huntsville Paragraph">
          Living in Huntsville, Ontario, immerses residents in a tranquil
          environment, surrounded by the natural splendor characteristic of the
          Muskoka region, not the hustle characteristic of places within the{' '}
          <LinkText href={cheapestLink}>Greater Toronto Area</LinkText>. The
          town is ideal for individuals who cherish the outdoors, offering a
          plethora of activities such as boating, hiking, and exploring nearby
          provincial parks. The close-knit community atmosphere is complemented
          by local arts, cultural events, and culinary experiences that cater to
          a simpler, relaxed lifestyle. While the housing market is varied,
          featuring everything from quaint cottages to suburban family
          residences, the area is particularly appealing to those who prefer a
          quieter, nature-centric existence. Unlike Metropolitan cities like{' '}
          <LinkText href={neighborhoodsLink}>Toronto</LinkText>, Huntsville
          represents a significant shift from urban living, offering a peaceful
          refuge nestled in nature’s embrace.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.huntsville-on:Popular Neighbourhoods Huntsville Paragraph">
          Like any city, Huntsville is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Downtown Huntsville, Waterfront Properties, North Huntsville,
          and South Huntsville. For more info about Huntsville, you can consult
          this guide on{' '}
          <LinkText href={neighborhoodsLink}>Toronto neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.huntsville-on:Local Landmarks Huntsville Paragraph">
          Huntsville, Ontario, is a treasure trove of landmarks that celebrate
          its rich cultural heritage and stunning natural surroundings. One
          prominent site is the Group of Seven Outdoor Gallery, a unique outdoor
          trail featuring over 90 mural replicas showcasing Canada’s famed Group
          of Seven painters. The historic downtown area itself is a landmark,
          with charming architecture, local boutiques, and eateries that reflect
          the town’s history and community spirit. For nature enthusiasts, the
          iconic Arrowhead Provincial Park offers scenic trails, a sparkling
          lake, and the famed ice skating trail in winter, making it a natural
          landmark within the town. Additionally, the Algonquin Theatre, a hub
          for local arts and culture, hosts a variety of performances and
          events, marking it as a cornerstone of community engagement and
          entertainment in Huntsville.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Huntsville
